import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Bot,
  Calendar,
  Pause,
  Bell,
  BookOpen,
  MessageSquare,
  Workflow,
  TrendingUp,
  CheckCircle2,
  Clock,
} from "lucide-react";

export default function Features() {
  return (
    <section
      id="funcionalidades"
      data-testid="features-section"
      className="relative py-16 sm:py-24 lg:py-32"
    >
      <div className="mx-auto max-w-5xl px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-card text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-300">
            <Workflow className="h-3.5 w-3.5" />
            Funcionalidades
          </div>
          <h2 className="mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.05]">
            Tudo o que precisa para
            <br />
            <span className="text-gradient-blue">automatizar com confiança.</span>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Card 1: WhatsApp Bot */}
          <FeatureCard>
            <CardHeader className="pb-3">
              <CardHeading
                icon={Bot}
                title="IA & Conversação"
                description="IA no WhatsApp que responde, lembra e escala para humano quando precisa."
              />
            </CardHeader>
            {/* WhatsApp chat mock */}
            <div className="mx-6 mb-6 rounded-xl overflow-hidden border border-white/[0.06]">
              {/* Chat header */}
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-[#075E54]/30 border-b border-white/[0.05]">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-[10px] font-bold text-white flex-none">A</div>
                <div>
                  <div className="text-xs font-medium text-slate-200">Automia</div>
                  <div className="text-[10px] text-green-400">online</div>
                </div>
              </div>
              {/* Messages */}
              <div className="bg-[#0a1628] px-3 py-3 space-y-2">
                <ChatBubble from="client" text="Bom dia! Têm horário para limpeza facial amanhã?" />
                <ChatBubble from="bot" text="Olá! Tenho às 10h00 ou às 14h30. Qual prefere?" />
                <ChatBubble from="client" text="14h30 perfeito!" />
                <ChatBubble from="bot" text="✅ Limpeza facial confirmada para amanhã às 14h30. Até já!" />
              </div>
            </div>
          </FeatureCard>

          {/* Card 2: Google Calendar */}
          <FeatureCard>
            <CardHeader className="pb-3">
              <CardHeading
                icon={Calendar}
                title="Google Calendar"
                description="Marcações em tempo real, sem conflitos, sem trocas de emails."
              />
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <CalendarMiniMock />
            </CardContent>
          </FeatureCard>

          {/* Card 3: Wide — stats dashboard */}
          <FeatureCard className="p-6 lg:col-span-2" persistentHover>
            <p className="mx-auto mt-4 mb-8 max-w-lg text-balance text-center text-2xl font-semibold text-white">
              Os seus clientes são atendidos. A agenda preenche-se. Pode descansar.
            </p>
            <StatsDashboard />
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}

const FeatureCard = ({ children, className, persistentHover = false }) => (
  <Card
    className={cn(
      "group relative rounded-2xl shadow-zinc-950/5 bg-[rgba(15,23,42,0.5)] backdrop-blur-xl border-white/[0.08] overflow-hidden",
      "transition-all duration-300 will-change-transform",
      "hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(59,130,246,0.08)]",
      persistentHover && "-translate-y-1 shadow-[0_8px_30px_rgba(59,130,246,0.08)]",
      className
    )}
  >
    <CardDecorator />

    {/* Dot pattern overlay — appears on hover */}
    <div className={cn(
      "absolute inset-0 transition-opacity duration-300 pointer-events-none",
      persistentHover ? "opacity-100" : "opacity-0 group-hover:opacity-100"
    )}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[length:4px_4px]" />
    </div>

    {children}

    {/* Gradient border overlay — appears on hover */}
    <div className={cn(
      "absolute inset-0 -z-10 rounded-2xl p-px bg-gradient-to-br from-transparent via-blue-500/10 to-transparent transition-opacity duration-300 pointer-events-none",
      persistentHover ? "opacity-100" : "opacity-0 group-hover:opacity-100"
    )} />
  </Card>
);

const CardDecorator = () => (
  <>
    <span className="border-blue-500/60 absolute -left-px -top-px block size-2 border-l-2 border-t-2" />
    <span className="border-blue-500/60 absolute -right-px -top-px block size-2 border-r-2 border-t-2" />
    <span className="border-blue-500/60 absolute -bottom-px -left-px block size-2 border-b-2 border-l-2" />
    <span className="border-blue-500/60 absolute -bottom-px -right-px block size-2 border-b-2 border-r-2" />
  </>
);

const CardHeading = ({ icon: Icon, title, description }) => (
  <div className="p-6">
    <span className="text-blue-300/70 flex items-center gap-2 text-sm">
      <Icon className="size-4" />
      {title}
    </span>
    <p className="mt-6 text-2xl font-semibold text-white leading-snug">{description}</p>
  </div>
);

function StatsDashboard() {
  const stats = [
    { label: "Horas poupadas por mês", value: "47h", change: "+47h livres", icon: Clock, color: "cyan" },
    { label: "Marcações criadas", value: "347", change: "+32%", icon: Calendar, color: "blue" },
    { label: "Tempo médio de resposta", value: "4s", change: "vs. 3h humano", icon: MessageSquare, color: "blue" },
    { label: "Escaladas para humano", value: "23", change: "98% autónomo", icon: Pause, color: "amber" },
  ];

  const recentActivity = [
    { text: "Marcação criada: Ana Costa, limpeza facial 10h00", time: "agora mesmo", ok: true },
    { text: "IA pausada: pergunta sobre produto específico", time: "3 min", ok: false },
    { text: "Lembrete enviado: Maria Lopes, hidratação 14h30", time: "8 min", ok: true },
    { text: "Remarcação: Sofia Reis, peeling 15h → 16h", time: "15 min", ok: true },
  ];

  return (
    <div className="grid sm:grid-cols-2 gap-4 pb-2">
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map(({ label, value, change, icon: Icon, color }) => (
          <div key={label} className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-3 ${
              color === "cyan" ? "bg-cyan-500/10 border border-cyan-500/20" :
              color === "amber" ? "bg-amber-500/10 border border-amber-500/20" :
              "bg-blue-500/10 border border-blue-500/20"
            }`}>
              <Icon className={`h-4 w-4 ${
                color === "cyan" ? "text-cyan-300" :
                color === "amber" ? "text-amber-300" :
                "text-blue-300"
              }`} />
            </div>
            <div className="text-2xl font-semibold text-white font-mono">{value}</div>
            <div className="text-[11px] text-slate-400 mt-0.5 leading-tight">{label}</div>
            <div className={`text-[10px] mt-2 font-medium flex items-center gap-1 ${
              change.startsWith("-") || change.startsWith("+") ? "text-emerald-400" : "text-slate-400"
            }`}>
              <TrendingUp className="h-3 w-3" />
              {change}
            </div>
          </div>
        ))}
      </div>

      {/* Activity feed */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Atividade recente</div>
        <div className="space-y-3">
          {recentActivity.map(({ text, time, ok }) => (
            <div key={text} className="flex items-start gap-2.5">
              <div className={`mt-0.5 h-4 w-4 rounded-full flex-none flex items-center justify-center ${
                ok ? "bg-emerald-500/15" : "bg-amber-500/15"
              }`}>
                {ok
                  ? <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                  : <Pause className="h-3 w-3 text-amber-400" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-300 leading-snug">{text}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ from, text }) {
  const isBot = from === "bot";
  return (
    <div className={`flex ${isBot ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
          isBot
            ? "bg-blue-500/15 border border-blue-500/20 text-blue-100 rounded-tl-sm"
            : "bg-white/[0.07] border border-white/[0.08] text-slate-300 rounded-tr-sm"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function CalendarMiniMock() {
  const days = ["S", "T", "Q", "Q", "S", "S", "D"];
  const dates = [
    null, null, 1, 2, 3, 4, 5,
    6, 7, 8, 9, 10, 11, 12,
    13, 14, 15, 16, 17, 18, 19,
    20, 21, 22, 23, 24, 25, 26,
  ];
  const booked = new Set([3, 8, 10, 14, 17, 22, 25]);
  const today = 14;

  const appointments = [
    { name: "Ana Costa", time: "10:00", service: "Consulta" },
    { name: "João Silva", time: "11:30", service: "Revisão" },
    { name: "Maria Lopes", time: "14:00", service: "Consulta" },
  ];

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      {/* Month header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.05]">
        <span className="text-xs font-medium text-slate-300">Março 2025</span>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
          <span className="text-[10px] text-slate-500">Google Calendar</span>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 px-3 pt-3">
        {days.map((d, i) => (
          <div key={i} className="text-center text-[10px] text-slate-500 pb-1.5">{d}</div>
        ))}
      </div>

      {/* Date grid */}
      <div className="grid grid-cols-7 px-3 pb-3 gap-y-0.5">
        {dates.map((d, i) => (
          <div key={i} className="flex items-center justify-center h-7">
            {d && (
              <div className={`relative h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-mono transition-colors
                ${d === today ? "bg-blue-500 text-white font-semibold" :
                  booked.has(d) ? "text-slate-300" : "text-slate-500"}`}
              >
                {d}
                {booked.has(d) && d !== today && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 w-0.5 rounded-full bg-blue-400" />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Upcoming appointments */}
      <div className="border-t border-white/[0.05] px-4 py-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Hoje · 14 Mar</div>
        {appointments.map(({ name, time, service }) => (
          <div key={name} className="flex items-center gap-3">
            <span className="text-[11px] font-mono text-blue-300 w-10 flex-none">{time}</span>
            <div className="flex-1 flex items-center justify-between min-w-0">
              <span className="text-xs text-slate-300 truncate">{name}</span>
              <span className="text-[10px] text-slate-500 ml-2 flex-none">{service}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
